import { Ng2StateDeclaration } from '@uirouter/angular';
import { Transition } from '@uirouter/core';

import { ScmHeaderComponent } from './header/scm.header.component';
import { ScmHomeComponent } from './home/scm.home.component';
import { ScmBrowseComponent } from './browse/scm.browse.component';
import { ScmCommitListComponent } from './commits/scm.commits.component';
import { ScmCommitDiffComponent } from './commit/scm.commit.component';
import { ScmMergeComponent } from './merge/scm.merge.component';
import { ScmFileViewComponent } from './file/scm.file.component';

import { ScmResolvesService } from './scm-resolves.service';

export const SCM_STATES: any[] = [
    {
        name: 'home.project.summary.scm',
        url: '/scm?branch&tag&commit&page_num&page_size&browse_path&browse_file',
        params: {
            // Last Browse location, carried across tabs so the Browse tab can return to it
            browse_path: {
                type: 'string',
                value: null,
                squash: true
            },
            browse_file: {
                type: 'bool',
                value: false,
                squash: true
            }
        },
        data: {
            searchContext: ['scm', 'scmcommit']
        },
        resolve: [
            {
                token: 'security',
                deps: ['project', ScmResolvesService],
                resolveFn: async (project: any, scmResolvesService: ScmResolvesService) => {
                    return scmResolvesService.security(project);
                }
            },
            {
                token: 'scm',
                deps: ['project', ScmResolvesService],
                resolveFn: async (project: any, scmResolvesService: ScmResolvesService) => {
                    return scmResolvesService.getScm(project);
                }
            },
            {
                token: 'breadcrumbs',
                deps: ['project', ScmResolvesService],
                resolveFn: async (project: any, scmResolvesService: ScmResolvesService) => {
                    return scmResolvesService.getInfoBreadcrumbs(project);
                }
            },
            {
                token: 'tour',
                deps: ['$transition$', 'scm', ScmResolvesService],
                resolveFn: async (transition: any, scm: any, scmResolvesService: ScmResolvesService) => {
                    return scmResolvesService.getHomeTour(transition, scm);
                }
            }
        ],
        views: {
            'projectheader@home.project.summary': {
                component: ScmHeaderComponent
            },
            'contentarea@home.project.summary': {
                component: ScmHomeComponent
            }
        }
    },
    {
        name: 'home.project.summary.scm.browse',
        url: '/browse/:path',
        params: {
            path: {
                type: 'string',
                raw: 'true',
                squash: true
            }
        },
        resolve: [
            {
                token: 'scmCheck',
                deps: ['$transition$', 'scm', ScmResolvesService],
                resolveFn: async (transition: any, scm: any, scmResolvesService: ScmResolvesService) => {
                    const params = transition.params();
                    return scmResolvesService.scmCheck(params.unixName, scm);
                }
            },
            {
                token: 'breadcrumbs',
                deps: ['$transition$', 'project', 'scm', ScmResolvesService],
                resolveFn: async (transition: any, project: any, scm: any, scmResolvesService: ScmResolvesService) => {
                    const params = transition.params();
                    return scmResolvesService.getBrowseBreadcrumbs(params, project, scm);
                }
            },
            {
                token: 'tour',
                deps: [ScmResolvesService],
                resolveFn: async (scmResolvesService: ScmResolvesService) => {
                    return scmResolvesService.getBrowseTour();
                }
            }
        ],
        views: {
            'projectheader@home.project.summary': {
                component: ScmHeaderComponent
            },
            'contentarea@home.project.summary': {
                component: ScmBrowseComponent
            }
        }
    },
    {
        name: 'home.project.summary.scm.commits',
        url: '/commits?module',
        params: {
            module: {
                type: 'string',
                value: '',
                squash: true
            }
        },
        resolve: [
            {
                token: 'scmCheck',
                deps: ['$transition$', 'scm', ScmResolvesService],
                resolveFn: async (transition: any, scm: any, scmResolvesService: ScmResolvesService) => {
                    const params = transition.params();
                    return scmResolvesService.scmCheck(params.unixName, scm);
                }
            },
            {
                token: 'breadcrumbs',
                deps: ['$transition$', 'project', 'scm', ScmResolvesService],
                resolveFn: async (transition: any, project: any, scm: any, scmResolvesService: ScmResolvesService) => {
                    const params = transition.params();
                    return scmResolvesService.getCommitsBreadcrumbs(params, project, scm);
                }
            }
        ],
        views: {
            'projectheader@home.project.summary': {
                component: ScmHeaderComponent
            },
            'contentarea@home.project.summary': {
                component: ScmCommitListComponent
            }
        }
    },
    {
        name: 'home.project.summary.scm.commit',
        url: '/commit/:commithash?module&view',
        reloadOnSearch: false,
        params: {
            module: {
                type: 'string',
                value: '',
                squash: true
            }
        },
        resolve: [
            {
                token: 'scmCheck',
                deps: ['$transition$', 'scm', ScmResolvesService],
                resolveFn: async (transition: any, scm: any, scmResolvesService: ScmResolvesService) => {
                    const params = transition.params();
                    return scmResolvesService.scmCheck(params.unixName, scm);
                }
            },
            {
                token: 'breadcrumbs',
                deps: ['$transition$', 'project', 'scm', ScmResolvesService],
                resolveFn: async (transition: any, project: any, scm: any, scmResolvesService: ScmResolvesService) => {
                    const params = transition.params();
                    return scmResolvesService.getCommitBreadcrumbs(params, project, scm);
                }
            }
        ],
        views: {
            'projectheader@home.project.summary': {
                component: ScmHeaderComponent
            },
            'contentarea@home.project.summary': {
                component: ScmCommitDiffComponent
            }
        }
    },
    {
        name: 'home.project.summary.scm.merge',
        url: '/merge?module&from&to&view',
        params: {
            module: {
                type: 'string',
                value: '',
                squash: true
            }
        },
        resolve: [
            {
                token: 'scmCheck',
                deps: ['$transition$', 'scm', ScmResolvesService],
                resolveFn: async (transition: any, scm: any, scmResolvesService: ScmResolvesService) => {
                    const params = transition.params();
                    return scmResolvesService.scmCheck(params.unixName, scm);
                }
            },
            {
                token: 'breadcrumbs',
                deps: ['$transition$', 'project', 'scm', ScmResolvesService],
                resolveFn: async (transition: any, project: any, scm: any, scmResolvesService: ScmResolvesService) => {
                    const params = transition.params();
                    return scmResolvesService.getMergeBreadcrumbs(params, project, scm);
                }
            }
        ],
        views: {
            'projectheader@home.project.summary': {
                component: ScmHeaderComponent
            },
            'contentarea@home.project.summary': {
                component: ScmMergeComponent
            }
        }
    },
    {
        name: 'home.project.summary.scm.file',
        url: '/file/:path?view',
        resolve: [
            {
                token: 'scmCheck',
                deps: ['$transition$', 'scm', ScmResolvesService],
                resolveFn: async (transition: any, scm: any, scmResolvesService: ScmResolvesService) => {
                    const params = transition.params();
                    return scmResolvesService.scmCheck(params.unixName, scm);
                }
            },
            {
                token: 'breadcrumbs',
                deps: ['$transition$', 'project', 'scm', ScmResolvesService],
                resolveFn: async (transition: any, project: any, scm: any, scmResolvesService: ScmResolvesService) => {
                    const params = transition.params();
                    return scmResolvesService.getBrowseBreadcrumbs(params, project, scm);
                }
            }
        ],
        params: {
            path: {
                type: 'string',
                raw: true,
                squash: true
            },
            view: {
                value: 'code',
                squash: true
            }
        },
        views: {
            'projectheader@home.project.summary': {
                component: ScmHeaderComponent
            },
            'contentarea@home.project.summary': {
                component: ScmFileViewComponent
            }
        }
    }
];